import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'
import { Spinner } from 'react-bootstrap'
import { FbModal as Modal } from 'components/UI/Modal'
import {
  checkDoorSelectionRules,
  selectDoorsStepFormSchema,
  SelectDoorsStepWithControl,
} from 'components/Deals/CreateDealModal/SelectDoorsStep'
import apiService from 'services/api'
import type { ApplyTo } from 'models/deals'

const editLocationsSchema = selectDoorsStepFormSchema.superRefine(
  checkDoorSelectionRules
)
type EditLocationsFormValues = z.infer<typeof editLocationsSchema>

interface EditDealLocationsModalProps {
  show: boolean
  onClose: () => void
  dealId: number
  contactCompanyId: number
  companyTotalDoors: number
  currentApplyTo: ApplyTo
  onSaved: () => void
}

/**
 * Focused modal for editing which locations a deal applies to.
 * Reuses SelectDoorsStepWithControl from the full EditDealModal.
 */
export function EditDealLocationsModal({
  show,
  onClose,
  dealId,
  contactCompanyId,
  companyTotalDoors,
  currentApplyTo,
  onSaved,
}: EditDealLocationsModalProps) {
  const api = apiService()

  const shouldFetchDoorIds = show && currentApplyTo === 'select_locations'
  const { data: doorIdsData, isLoading: isDoorIdsLoading } =
    api.useGetSelectedDoorIdsForDeal(dealId, shouldFetchDoorIds, {
      noCache: true,
    })

  const initialDoorIds = useMemo(() => {
    if (currentApplyTo === 'select_locations') {
      return doorIdsData?.ids ?? []
    }
    return []
  }, [currentApplyTo, doorIdsData?.ids])

  const isReady = !shouldFetchDoorIds || !isDoorIdsLoading

  if (!show) return null

  if (!isReady) {
    return (
      <Modal
        open={true}
        onOpenChange={(open) => !open && onClose()}
        title="Edit Locations"
        size="lg"
        onCancel={onClose}
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-2">
            <Spinner animation="border" />
            <span className="text-fg-secondary text-sm">
              Loading locations...
            </span>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <EditLocationsFormContent
      key={`${dealId}-${initialDoorIds.length}`}
      dealId={dealId}
      contactCompanyId={contactCompanyId}
      companyTotalDoors={companyTotalDoors}
      currentApplyTo={currentApplyTo}
      initialDoorIds={initialDoorIds}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}

interface EditLocationsFormContentProps {
  dealId: number
  contactCompanyId: number
  companyTotalDoors: number
  currentApplyTo: ApplyTo
  initialDoorIds: number[]
  onClose: () => void
  onSaved: () => void
}

function EditLocationsFormContent({
  dealId,
  contactCompanyId,
  companyTotalDoors,
  currentApplyTo,
  initialDoorIds,
  onClose,
  onSaved,
}: EditLocationsFormContentProps) {
  const api = apiService()

  const form = useForm<EditLocationsFormValues>({
    resolver: zodResolver(editLocationsSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      apply_to: currentApplyTo,
      door_ids: initialDoorIds,
    },
  })

  const {
    formState: { isValid, isSubmitting },
  } = form

  const onSubmit = form.handleSubmit(async (values) => {
    const body: Record<string, unknown> = { apply_to: values.apply_to }
    if (values.apply_to === 'select_locations') {
      body.door_ids = values.door_ids
    }

    try {
      await api.editDeal(dealId, body)
      toast.success('Locations updated')
      onClose()
      onSaved()
    } catch {
      toast.error('Failed to update locations')
    }
  })

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      title="Edit Locations"
      size="lg"
      onAccept={onSubmit}
      onCancel={onClose}
      acceptButtonText="Save"
      loading={isSubmitting}
      acceptButtonProps={{ disabled: !isValid }}
    >
      <SelectDoorsStepWithControl
        control={form.control}
        chainProxyId={contactCompanyId}
        companyTotalDoors={companyTotalDoors}
        formTrigger={form.trigger}
      />
    </Modal>
  )
}
